import { Grid } from '@mui/material';
import { useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useKwalificatieMutaties } from '../../api/kandidaten.ts';
import type {
  Bedrijf,
  Certificaattype,
  CvDetails,
  Functietitel,
  KandidaatProfiel,
  Opleiding,
  Opleidingsniveau,
} from '../../api/typen.ts';
import { FormulierDialoog } from '../../componenten/FormulierDialoog.tsx';
import { BedrijfVeld, CertificaattypeVeld, FunctietitelVeld } from '../../componenten/keuzevelden.tsx';
import { Keuzeveld, Tekstveld } from '../../componenten/velden.tsx';
import { leegNaarNull, naarGetal, naarTekst } from '../../helpers/formulier.ts';
import { opleidingsniveauLabels, opties } from '../../helpers/labels.ts';
import { useVerzenden } from '../../hooks/useVerzenden.ts';

type WerkervaringMetDetails = KandidaatProfiel['werkervaringen'][number];
type CertificaatMetDetails = KandidaatProfiel['certificaten'][number];

interface Basisprops {
  open: boolean;
  onSluit: () => void;
  kandidaatId: string;
  cvs: readonly CvDetails[];
}

function cvOpties(cvs: readonly CvDetails[]) {
  return cvs.map((cv) => ({ waarde: cv.id, label: `${cv.bestandsnaam}${cv.isActueel ? ' (actueel)' : ''}` }));
}

interface WerkervaringWaarden {
  functietitelLetterlijk: string;
  functietitel: Functietitel | null;
  werkgever: string;
  bedrijf: Bedrijf | null;
  van: string;
  tot: string;
  omschrijving: string;
  cvId: string;
}

function werkervaringBegin(bestaand: WerkervaringMetDetails | undefined): WerkervaringWaarden {
  return {
    functietitelLetterlijk: bestaand?.functietitelLetterlijk ?? '',
    functietitel: bestaand?.functietitel ?? null,
    werkgever: bestaand?.werkgever ?? '',
    bedrijf: bestaand?.bedrijf ?? null,
    van: bestaand?.van ?? '',
    tot: bestaand?.tot ?? '',
    omschrijving: bestaand?.omschrijving ?? '',
    cvId: bestaand?.cvId ?? '',
  };
}

/**
 * Voegt werkervaring toe aan een kandidaat of wijzigt die. Zonder gekozen functietitel probeert de API de
 * functietitel te herkennen uit de letterlijke titel.
 * @param props De kandidaat, zijn cv's en de bestaande werkervaring om te wijzigen.
 * @returns De dialoog.
 */
export function WerkervaringFormulier({
  open,
  onSluit,
  kandidaatId,
  cvs,
  bestaand,
}: Basisprops & { bestaand?: WerkervaringMetDetails | undefined }) {
  const mutaties = useKwalificatieMutaties(kandidaatId);
  const formulier = useForm<WerkervaringWaarden>({ defaultValues: werkervaringBegin(bestaand) });
  useEffect(() => {
    if (open) {
      formulier.reset(werkervaringBegin(bestaand));
    }
  }, [open, bestaand, formulier]);

  const { onVerzend, fout, bezig } = useVerzenden(
    formulier,
    (waarden) => {
      const invoer = {
        functietitelLetterlijk: waarden.functietitelLetterlijk,
        functietitelId: waarden.functietitel?.id ?? null,
        werkgever: leegNaarNull(waarden.werkgever),
        bedrijfId: waarden.bedrijf?.id ?? null,
        cvId: leegNaarNull(waarden.cvId),
        van: leegNaarNull(waarden.van),
        tot: leegNaarNull(waarden.tot),
        omschrijving: leegNaarNull(waarden.omschrijving),
      };
      return bestaand
        ? mutaties.werkervaring.wijzig.mutateAsync({ id: bestaand.id, invoer })
        : mutaties.werkervaring.voegToe.mutateAsync(invoer);
    },
    { melding: 'Werkervaring opgeslagen', naSucces: onSluit },
  );

  return (
    <FormProvider {...formulier}>
      <FormulierDialoog
        open={open}
        titel={bestaand ? 'Werkervaring wijzigen' : 'Werkervaring toevoegen'}
        onSluit={onSluit}
        onVerzend={onVerzend}
        fout={fout}
        bezig={bezig}
        maxWidth="md"
      >
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <Tekstveld naam="functietitelLetterlijk" label="Functietitel zoals in het cv" verplicht autoFocus />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <FunctietitelVeld
              naam="functietitel"
              label="Genormaliseerde functietitel"
              hulptekst="Leeg: wordt herkend als dat lukt"
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <Tekstveld naam="werkgever" label="Werkgever" />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <BedrijfVeld naam="bedrijf" label="Bedrijf in het CRM" />
          </Grid>
          <Grid size={{ xs: 6, sm: 3 }}>
            <Tekstveld naam="van" label="Van" type="date" />
          </Grid>
          <Grid size={{ xs: 6, sm: 3 }}>
            <Tekstveld naam="tot" label="Tot" type="date" />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <Keuzeveld naam="cvId" label="Uit cv" opties={cvOpties(cvs)} leegLabel="Geen cv" />
          </Grid>
          <Grid size={12}>
            <Tekstveld naam="omschrijving" label="Omschrijving" meerregelig rijen={3} />
          </Grid>
        </Grid>
      </FormulierDialoog>
    </FormProvider>
  );
}

interface OpleidingWaarden {
  niveau: Opleidingsniveau | '';
  richting: string;
  instelling: string;
  eindjaar: string;
  diplomaBehaald: 'ja' | 'nee' | '';
  cvId: string;
}

const niveauOpties = opties(opleidingsniveauLabels);
const diplomaOpties = [
  { waarde: 'ja', label: 'Ja' },
  { waarde: 'nee', label: 'Nee' },
] as const;

function opleidingBegin(bestaand: Opleiding | undefined): OpleidingWaarden {
  return {
    niveau: bestaand?.niveau ?? '',
    richting: bestaand?.richting ?? '',
    instelling: bestaand?.instelling ?? '',
    eindjaar: naarTekst(bestaand?.eindjaar),
    diplomaBehaald:
      bestaand?.diplomaBehaald === null || bestaand?.diplomaBehaald === undefined
        ? ''
        : bestaand.diplomaBehaald
          ? 'ja'
          : 'nee',
    cvId: bestaand?.cvId ?? '',
  };
}

/**
 * Voegt een opleiding toe aan een kandidaat of wijzigt die.
 * @param props De kandidaat, zijn cv's en de bestaande opleiding om te wijzigen.
 * @returns De dialoog.
 */
export function OpleidingFormulier({
  open,
  onSluit,
  kandidaatId,
  cvs,
  bestaand,
}: Basisprops & { bestaand?: Opleiding | undefined }) {
  const mutaties = useKwalificatieMutaties(kandidaatId);
  const formulier = useForm<OpleidingWaarden>({ defaultValues: opleidingBegin(bestaand) });
  useEffect(() => {
    if (open) {
      formulier.reset(opleidingBegin(bestaand));
    }
  }, [open, bestaand, formulier]);

  const { onVerzend, fout, bezig } = useVerzenden(
    formulier,
    (waarden) => {
      const invoer = {
        niveau: waarden.niveau || null,
        richting: leegNaarNull(waarden.richting),
        instelling: leegNaarNull(waarden.instelling),
        eindjaar: naarGetal(waarden.eindjaar),
        diplomaBehaald: waarden.diplomaBehaald === '' ? null : waarden.diplomaBehaald === 'ja',
        cvId: leegNaarNull(waarden.cvId),
      };
      return bestaand
        ? mutaties.opleiding.wijzig.mutateAsync({ id: bestaand.id, invoer })
        : mutaties.opleiding.voegToe.mutateAsync(invoer);
    },
    { melding: 'Opleiding opgeslagen', naSucces: onSluit },
  );

  return (
    <FormProvider {...formulier}>
      <FormulierDialoog
        open={open}
        titel={bestaand ? 'Opleiding wijzigen' : 'Opleiding toevoegen'}
        onSluit={onSluit}
        onVerzend={onVerzend}
        fout={fout}
        bezig={bezig}
      >
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <Keuzeveld naam="niveau" label="Niveau" opties={niveauOpties} />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <Tekstveld naam="richting" label="Richting" autoFocus />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <Tekstveld naam="instelling" label="Instelling" />
          </Grid>
          <Grid size={{ xs: 6, sm: 3 }}>
            <Tekstveld naam="eindjaar" label="Eindjaar" type="number" min={1900} max={2200} />
          </Grid>
          <Grid size={{ xs: 6, sm: 3 }}>
            <Keuzeveld naam="diplomaBehaald" label="Diploma behaald" opties={diplomaOpties} leegLabel="Onbekend" />
          </Grid>
          <Grid size={12}>
            <Keuzeveld naam="cvId" label="Uit cv" opties={cvOpties(cvs)} leegLabel="Geen cv" />
          </Grid>
        </Grid>
      </FormulierDialoog>
    </FormProvider>
  );
}

interface CertificaatWaarden {
  certificaattype: Certificaattype | null;
  behaaldOp: string;
  geldigTot: string;
  cvId: string;
}

function certificaatBegin(bestaand: CertificaatMetDetails | undefined): CertificaatWaarden {
  return {
    certificaattype: bestaand?.certificaattype ?? null,
    behaaldOp: bestaand?.behaaldOp ?? '',
    geldigTot: bestaand?.geldigTot ?? '',
    cvId: bestaand?.cvId ?? '',
  };
}

/**
 * Voegt een certificaat toe aan een kandidaat of wijzigt het. Zonder einddatum berekent de API die uit de
 * geldigheid van het type.
 * @param props De kandidaat, zijn cv's en het bestaande certificaat om te wijzigen.
 * @returns De dialoog.
 */
export function CertificaatFormulier({
  open,
  onSluit,
  kandidaatId,
  cvs,
  bestaand,
}: Basisprops & { bestaand?: CertificaatMetDetails | undefined }) {
  const mutaties = useKwalificatieMutaties(kandidaatId);
  const formulier = useForm<CertificaatWaarden>({ defaultValues: certificaatBegin(bestaand) });
  useEffect(() => {
    if (open) {
      formulier.reset(certificaatBegin(bestaand));
    }
  }, [open, bestaand, formulier]);

  const { onVerzend, fout, bezig } = useVerzenden(
    formulier,
    (waarden) => {
      if (!waarden.certificaattype) {
        throw new Error('Kies een certificaattype.');
      }
      const invoer = {
        certificaattypeId: waarden.certificaattype.id,
        behaaldOp: leegNaarNull(waarden.behaaldOp),
        geldigTot: leegNaarNull(waarden.geldigTot),
        cvId: leegNaarNull(waarden.cvId),
      };
      return bestaand
        ? mutaties.certificaat.wijzig.mutateAsync({ id: bestaand.id, invoer })
        : mutaties.certificaat.voegToe.mutateAsync(invoer);
    },
    { melding: 'Certificaat opgeslagen', naSucces: onSluit },
  );

  return (
    <FormProvider {...formulier}>
      <FormulierDialoog
        open={open}
        titel={bestaand ? 'Certificaat wijzigen' : 'Certificaat toevoegen'}
        onSluit={onSluit}
        onVerzend={onVerzend}
        fout={fout}
        bezig={bezig}
      >
        <Grid container spacing={2}>
          <Grid size={12}>
            <CertificaattypeVeld naam="certificaattype" verplicht autoFocus />
          </Grid>
          <Grid size={{ xs: 6 }}>
            <Tekstveld naam="behaaldOp" label="Behaald op" type="date" />
          </Grid>
          <Grid size={{ xs: 6 }}>
            <Tekstveld naam="geldigTot" label="Geldig tot" type="date" hulptekst="Leeg: volgt uit het type" />
          </Grid>
          <Grid size={12}>
            <Keuzeveld naam="cvId" label="Uit cv" opties={cvOpties(cvs)} leegLabel="Geen cv" />
          </Grid>
        </Grid>
      </FormulierDialoog>
    </FormProvider>
  );
}
